/**
 * Permission Constants - Frontend mirror of backend permission templates
 *
 * This is a frontend copy of utils/permission_constants.py for fallback
 * permission checking when PermissionContext is not available.
 *
 * IMPORTANT: Role templates here store the EFFECTIVE (post-inheritance)
 * permissions. The backend uses ROLE_HIERARCHY for inheritance; here we
 * expand them so the frontend never needs to resolve inheritance itself.
 */

export const PERMISSION_CATEGORIES = {
  leads: ['view', 'create', 'edit', 'delete', 'convert', 'assign'],
  scheduling: ['view', 'create', 'edit', 'delete', 'reschedule', 'manage'],
  patients: ['view', 'create', 'edit', 'delete', 'scope_org', 'scope_team', 'scope_assigned'],
  billing: ['view', 'create', 'edit', 'process_payment', 'manage_allocation', 'export', 'scope_org', 'scope_team', 'scope_self'],
  notes: ['view', 'create', 'edit', 'approve', 'sign', 'view_metadata', 'scope_org', 'scope_team', 'scope_assigned'],
  revenue: ['view', 'export', 'analytics', 'scope_org', 'scope_team', 'scope_self'],
  reports: ['view', 'create', 'export', 'analytics', 'scope_org', 'scope_self'],
  messaging: ['view', 'send', 'delete'],
  settings: ['view', 'edit', 'users', 'payroll', 'billing'],
  payroll: ['manage'],
  provider_settings: ['view', 'edit'],
  email_templates: ['view', 'create', 'edit', 'delete'],
  dashboard: ['customize'],
  documents: ['view', 'upload', 'delete'],
  health_tracking: ['view'],
  medications: ['view', 'edit'],
  diagnosis: ['view', 'edit'],
  risk_assessment: ['view', 'create'],
  reflections: ['view', 'create'],
  equipment: ['view', 'manage'],
  locations: ['view', 'manage'],
  intake_checklist: ['view', 'edit'],
  invitations: ['send'],
  analytics: ['view_financial'],
  staff: ['access'],
  // Platform-level permissions (cross-org access)
  platform: ['admin', 'dev', 'owner'],
  // Timesheet-specific scope permissions
  timesheets: ['view_all', 'view_team', 'view_self', 'approve'],
  // Backup system permissions
  backup: ['view', 'create', 'verify', 'restore', 'delete'],
};

// ---------------------------------------------------------------------------
// Billing Tier Add-On System
//
// Billing permissions are NOT baked into clinical role templates (provider,
// medical_assistant, student, supervisor). Instead they are granted as a
// toggleable "tier" add-on assigned per-role or per-user.
// ---------------------------------------------------------------------------

export const BILLING_TIERS = {
  none: {
    name: 'No Billing Access',
    description: 'No billing permissions (default for clinical roles)',
    permissions: [] as string[],
  },
  tier_1: {
    name: 'Collect Payments',
    description:
      'Day-to-day billing: view balances & outstanding list, create/edit invoices, collect payments. No aggregate revenue data or exports.',
    permissions: [
      'billing.view',
      'billing.create',
      'billing.edit',
      'billing.process_payment',
      'billing.manage_allocation',
    ],
  },
  tier_2: {
    name: 'Full Billing',
    description:
      'Complete billing access: all Tier 1 operations plus billing export, revenue analytics, and financial reports.',
    permissions: [
      'billing.view',
      'billing.create',
      'billing.edit',
      'billing.process_payment',
      'billing.manage_allocation',
      'billing.export',
      'revenue.view',
      'revenue.export',
      'revenue.analytics',
      'analytics.view_financial',
      'reports.view',
      'reports.export',
      'reports.analytics',
    ],
  },
} as const;

export type BillingTierKey = keyof typeof BILLING_TIERS;

/** Roles eligible for the billing tier add-on. */
export const BILLING_TIER_ELIGIBLE_ROLES = [
  'provider',
  'medical_assistant',
  'student',
  'supervised_provider',
  'supervisor',
  'billing_specialist',
] as const;

/** Set version — preferred for `.has()` lookups. */
export const BILLING_TIER_ELIGIBLE_ROLES_SET = new Set<string>(BILLING_TIER_ELIGIBLE_ROLES);

/** Roles that already include full billing permissions (no add-on needed). */
export const BILLING_BAKED_IN_ROLES = new Set<string>([
  'admin',
  'clinical_director',
]);

export const ROLE_TEMPLATES = {
  // NOTE: client role is NOT part of the EHR RBAC system.
  // Client portal access is controlled entirely by PatientRelationship
  // (per-patient flags: can_view_billing, can_sign_forms, etc.).
  client: {
  },
  intake_coordinator: {
    'billing.scope_org': true, // org-wide billing visibility for intake workflows
    'billing.view': true,
    'dashboard.customize': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'email_templates.view': true,
    'invitations.send': true,
    'leads.assign': true,
    'leads.convert': true,
    'leads.create': true,
    'leads.delete': true,
    'leads.edit': true,
    'leads.view': true,
    'locations.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_org': true, // sees all patients for intake/scheduling
    'patients.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
  },
  billing_specialist: {
    'billing.create': true,
    'billing.edit': true,
    'billing.process_payment': true,
    'billing.scope_org': true, // org-wide billing data access
    'billing.view': true,
    'dashboard.customize': true,
    'leads.view': true,
    'locations.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_org': true, // sees all patients for billing
    'patients.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
  },
  // Standalone read-only financial role for external accountants/bookkeepers.
  // No inheritance — self-contained with view + export only.
  accountant: {
    'billing.export': true,
    'billing.scope_org': true, // org-wide billing data (read-only)
    'billing.view': true,
    'dashboard.customize': true,
    'locations.view': true,
    'payroll.manage': true,
    'reports.export': true,
    'reports.scope_org': true,
    'reports.view': true,
    'revenue.analytics': true,
    'revenue.export': true,
    'revenue.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
  },
  medical_assistant: {
    // Billing: removed — granted via Billing Tier Add-On
    'dashboard.customize': true,
    'diagnosis.edit': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'email_templates.view': true,
    'equipment.view': true,
    'health_tracking.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'locations.view': true,
    'medications.edit': true,
    'medications.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.create': true,
    'notes.edit': true,
    'notes.scope_team': true, // sees assigned NPs' notes
    'notes.sign': true, // can sign own notes (not NP notes)
    'notes.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_team': true, // sees assigned NPs' patients
    'patients.view': true,
    'reports.view': true,
    'risk_assessment.create': true,
    'risk_assessment.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
  },
  provider: {
    // Billing: removed — granted via Billing Tier Add-On
    'dashboard.customize': true,
    'diagnosis.edit': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'email_templates.view': true,
    'equipment.view': true,
    'health_tracking.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'locations.view': true,
    'medications.edit': true,
    'medications.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.approve': true, // providers can co-sign supervised clinicians' notes
    'notes.create': true,
    'notes.edit': true,
    'notes.scope_assigned': true, // sees only assigned patients' notes
    'notes.sign': true,
    'notes.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_assigned': true, // sees only assigned patients
    'patients.view': true,
    'reflections.create': true,
    'reflections.view': true,
    'reports.scope_self': true, // own reports only
    'reports.view': true,
    'risk_assessment.create': true,
    'risk_assessment.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
  },
  admin: {
    'analytics.view_financial': true,
    'billing.create': true,
    'billing.edit': true,
    'billing.export': true,
    'billing.process_payment': true,
    'billing.scope_org': true, // admins see all org billing
    'billing.view': true,
    'dashboard.customize': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'email_templates.create': true,
    'email_templates.delete': true,
    'email_templates.edit': true,
    'email_templates.view': true,
    'equipment.manage': true,
    'equipment.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'invitations.send': true,
    'leads.assign': true,
    'leads.convert': true,
    'leads.create': true,
    'leads.delete': true,
    'leads.edit': true,
    'leads.view': true,
    'locations.manage': true,
    'locations.view': true,
    'messaging.delete': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.delete': true,
    'patients.edit': true,
    'patients.scope_org': true, // admins see all patients in org
    'patients.view': true,
    'reports.analytics': true,
    'reports.create': true,
    'reports.export': true,
    'reports.scope_org': true, // org-wide reports
    'reports.view': true,
    'revenue.analytics': true,
    'revenue.export': true,
    'revenue.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'settings.edit': true,
    'settings.payroll': true,
    'settings.users': true,
    'settings.view': true,
    'staff.access': true,
    'timesheets.approve': true,
    'timesheets.view_all': true,
  },
  office_manager: {
    'billing.create': true,
    'billing.edit': true,
    'billing.export': true,
    'billing.process_payment': true,
    'billing.scope_org': true, // office managers see all org billing
    'billing.view': true,
    'dashboard.customize': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'email_templates.view': true,
    'equipment.manage': true,
    'equipment.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'invitations.send': true,
    'leads.assign': true,
    'leads.convert': true,
    'leads.create': true,
    'leads.delete': true,
    'leads.edit': true,
    'leads.view': true,
    'locations.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_org': true, // office managers see all patients
    'patients.view': true,
    'reports.scope_org': true, // org-wide reports
    'reports.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
  },
  // Effective permissions after inheriting from provider.
  // Students cannot: co-sign notes, edit medications, view email templates.
  // Billing: handled by Billing Tier Add-On (not in base template).
  student: {
    'dashboard.customize': true,
    'diagnosis.edit': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'equipment.view': true,
    'health_tracking.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'locations.view': true,
    'medications.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.create': true,
    'notes.edit': true,
    'notes.scope_assigned': true,
    'notes.sign': true,
    'notes.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_assigned': true,
    'patients.view': true,
    'reflections.create': true,
    'reflections.view': true,
    'reports.scope_self': true,
    'reports.view': true,
    'risk_assessment.create': true,
    'risk_assessment.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
  },
  // Effective permissions after inheriting from provider.
  // Supervised providers cannot: co-sign notes, edit medications, view email templates.
  // Billing: handled by Billing Tier Add-On (not in base template).
  supervised_provider: {
    'dashboard.customize': true,
    'diagnosis.edit': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'equipment.view': true,
    'health_tracking.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'locations.view': true,
    'medications.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.create': true,
    'notes.edit': true,
    'notes.scope_assigned': true,
    'notes.sign': true,
    'notes.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_assigned': true,
    'patients.view': true,
    'reflections.create': true,
    'reflections.view': true,
    'reports.scope_self': true,
    'reports.view': true,
    'risk_assessment.create': true,
    'risk_assessment.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
    // billing is disabled by default for supervised providers (clinic can toggle on)
  },
  // Effective permissions after inheriting from provider.
  // Supervisors get team-wide scope (own + supervisees).
  // Billing: handled by Billing Tier Add-On (not in base template).
  supervisor: {
    'dashboard.customize': true,
    'diagnosis.edit': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'email_templates.view': true,
    'equipment.view': true,
    'health_tracking.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'locations.view': true,
    'medications.edit': true,
    'medications.view': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.approve': true,
    'notes.create': true,
    'notes.edit': true,
    'notes.scope_team': true, // own + supervisees' notes
    'notes.sign': true,
    'notes.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.edit': true,
    'patients.scope_team': true, // own + supervisees' patients
    'patients.view': true,
    'reflections.create': true,
    'reflections.view': true,
    'reports.scope_self': true,
    'reports.view': true,
    'risk_assessment.create': true,
    'risk_assessment.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'staff.access': true,
    'timesheets.view_self': true,
    'timesheets.view_team': true,
  },
  // Effective permissions after inheriting from supervisor + admin.
  // Clinical directors get org-wide scope for everything.
  clinical_director: {
    'analytics.view_financial': true,
    'billing.create': true,
    'billing.edit': true,
    'billing.export': true,
    'billing.process_payment': true,
    'billing.scope_org': true, // org-wide billing
    'billing.view': true,
    'dashboard.customize': true,
    'diagnosis.edit': true,
    'diagnosis.view': true,
    'documents.delete': true,
    'documents.upload': true,
    'documents.view': true,
    'email_templates.create': true,
    'email_templates.delete': true,
    'email_templates.edit': true,
    'email_templates.view': true,
    'equipment.manage': true,
    'equipment.view': true,
    'health_tracking.view': true,
    'intake_checklist.edit': true,
    'intake_checklist.view': true,
    'invitations.send': true,
    'leads.assign': true,
    'leads.convert': true,
    'leads.create': true,
    'leads.delete': true,
    'leads.edit': true,
    'leads.view': true,
    'locations.manage': true,
    'locations.view': true,
    'medications.edit': true,
    'medications.view': true,
    'messaging.delete': true,
    'messaging.send': true,
    'messaging.view': true,
    'notes.approve': true,
    'notes.create': true,
    'notes.edit': true,
    'notes.scope_org': true, // org-wide note access (clinical role)
    'notes.sign': true,
    'notes.view': true,
    'notes.view_metadata': true,
    'patients.create': true,
    'patients.delete': true,
    'patients.edit': true,
    'patients.scope_org': true, // org-wide patient access
    'patients.view': true,
    'payroll.manage': true, // org-wide payroll administration
    'reflections.create': true,
    'reflections.view': true,
    'reports.analytics': true,
    'reports.create': true,
    'reports.export': true,
    'reports.scope_org': true, // org-wide reports
    'reports.scope_self': true,
    'reports.view': true,
    'revenue.analytics': true,
    'revenue.export': true,
    'revenue.view': true,
    'risk_assessment.create': true,
    'risk_assessment.view': true,
    'scheduling.create': true,
    'scheduling.delete': true,
    'scheduling.edit': true,
    'scheduling.manage': true,
    'scheduling.reschedule': true,
    'scheduling.view': true,
    'settings.edit': true,
    'settings.payroll': true,
    'settings.users': true,
    'settings.view': true,
    'staff.access': true,
    'timesheets.approve': true,
    'timesheets.view_all': true,
    'timesheets.view_self': true,
    'timesheets.view_team': true,
  },
  dev: {
    'backup.create': true,
    'backup.delete': true,
    'backup.restore': true,
    'backup.verify': true,
    'backup.view': true,
    'dashboard.customize': true,
    'platform.dev': true,
    'staff.access': true,
  },
  platform_owner: {
    'backup.create': true,
    'backup.delete': true,
    'backup.restore': true,
    'backup.verify': true,
    'backup.view': true,
    'dashboard.customize': true,
    'platform.admin': true,
    'platform.owner': true,
    'staff.access': true,
    // No clinical permissions - platform owners see aggregate metrics only
  },
};

/**
 * Get human-readable display name for permission
 * @param {string} permission - Permission key (e.g., "leads.view")
 * @returns {string} Display name (e.g., "Leads: View")
 */
export const getPermissionDisplayName = (permission: string): string => {
  if (!permission.includes('.')) {
    return permission.charAt(0).toUpperCase() + permission.slice(1);
  }

  const [category, action] = permission.split('.');
  const categoryName = category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const actionName = action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  return `${categoryName}: ${actionName}`;
};

/**
 * Get all permissions organized by category
 * @returns {Object} Category to permission keys mapping
 */
export const getPermissionsByCategory = (): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  for (const [category, actions] of Object.entries(PERMISSION_CATEGORIES)) {
    result[category] = actions.map(action => `${category}.${action}`);
  }
  return result;
};

/**
 * Validate that a permission string is well-formed
 * @param {string} permission - Permission string to validate
 * @returns {boolean} True if valid
 */
export const validatePermission = (permission: string): boolean => {
  if (!permission || !permission.includes('.')) {
    return false;
  }

  const [category, action] = permission.split('.');
  return (
    PERMISSION_CATEGORIES[category as keyof typeof PERMISSION_CATEGORIES] !== undefined &&
    PERMISSION_CATEGORIES[category as keyof typeof PERMISSION_CATEGORIES].includes(action)
  );
};
